import { DatasetSource, DenominatorLabelValues } from '@services/api/deckManagement/graphql/schema/API';
import { ReactNode } from 'react';
import { LockStatus } from 'types/DeckContext.types';
import { PageGranularity } from 'types/Page.types';
import { AnyKPIDrivers } from 'types/PageSidePanel.types';
import { EnhancedTOCEntry } from 'types/TOCContext.types';

export enum PageSetupSteps {
  PAGE_DETAILS = 'Page Details',
  TABLE_CONFIGURATION = 'Table Configuration',
  PAGE_DRIVER = 'Page Driver',
}

export interface PageSetupContextType {
  // Stepper state
  pageSetupSteps: PageSetupSteps[];
  currentStep: PageSetupSteps | null;
  nextStep: PageSetupSteps | null;
  setCurrentStep: (currentStep: PageSetupSteps) => void;

  // Route-derived state
  currDeckId: string | null;
  currPageName: string | null;

  // Created or existing TOC entry ID
  tocId: string | null;
  setTocId: (tocId: string | null) => void;
  // Resolved TOC entry from the current modifiedTocEntries (used to obtain latest internalId after initial creation)
  tocEntry: EnhancedTOCEntry | undefined;
  // Indicates whether a TOCEntry and its underlying grain Pages have been created by the backend
  isPageCreated: boolean;
  setIsPageCreated: (value: boolean) => void;

  // Page Details Step state
  pageName: string;
  setPageName: (newPageName: string) => void;
  tocName: string;
  setTocName: (newTocName: string) => void;
  pageHeaderName: string;
  setPageHeaderName: (newPageHeaderName: string) => void;
  reportingPeriods: PageGranularity[];
  setReportingPeriods: (newReportingPeriods: PageGranularity[]) => void;
  /** Tracks denominator_label field of Page Entity and associated Page Setup Wizard Step **/
  denominatorLabel: DenominatorLabelValues | null;
  setDenominatorLabel: (newDenominatorLabel: DenominatorLabelValues | null) => void;

  // Table Configuration Step state
  /**
   * Selected reusable PageLayout family for the current page setup.
   *
   * The same family is assigned to every Page grain created by SaveDeck.
   * The catalog selector owns choosing this value; custom tree authoring is
   * intentionally out of scope for this initial path.
   */
  selectedLayoutFamily: string;
  setSelectedLayoutFamily: (layoutFamily: string) => void;
  /**
   * Assigns selectedLayoutFamily to every created Page grain through SaveDeck.
   */
  assignLayoutFamily: (successCallback?: () => void, failureCallback?: (error: Error) => void) => void;
  /** True while the SaveDeck page.update family assignment is in flight. */
  isLayoutFamilySaving: boolean;

  /** Whether the layout template selection has changed from its initial persisted state */
  hasTableConfigChanged: boolean;
  /** Updates the baseline snapshot after a successful save */
  snapshotTableConfig: () => void;
  /** Restores the layout template selection to the initial persisted state */
  resetTableConfig: () => void;
  /** Whether we are dealing with an Update request (set true after first save or when editing an existing page) */
  isUpdateTemplate: boolean;
  setIsUpdateTemplate: (value: boolean) => void;

  /** Dynamically determined available grains from pagesByName */
  availableGrains: string[];

  // Page Drivers Step state
  /**
   * All configured page-driver sets, keyed by dataset (GALAXY included) — the single source of
   * truth for the Page Driver step. GALAXY-is-default handling (always shown, never removable) and
   * add/remove/update live in the view (`PageDriverStep`), not here.
   */
  pageDriverSets: Partial<Record<DatasetSource, AnyKPIDrivers>>;
  /** Setter for the configured page-driver sets map. */
  setPageDriverSets: React.Dispatch<React.SetStateAction<Partial<Record<DatasetSource, AnyKPIDrivers>>>>;
  /** Datasets the deck permits a page to configure (from `Deck.enabled_datasets`; GALAXY is the default). */
  enabledDatasets: DatasetSource[];
  /**
   * Number of decklines on the page bound to each dataset (base-KPI decklines only; blank/calculated
   * lines bind no dataset). Empty for a brand-new page. The Page Driver step uses this to block
   * removing a dataset source that decklines still depend on.
   */
  decklineDatasetCounts: Partial<Record<DatasetSource, number>>;
  /** Persists page driver changes via SaveDeck mutation */
  savePageDrivers: (successCallback?: () => void, failureCallback?: (error: Error) => void) => void;
  /** Whether a save operation is currently in progress */
  pageDriversSaving: boolean;
  /** Error message from Page-related requests (if any) */
  pageError: string | null;
  /**
   * Lock state for the page being set up (all grains share one lock). Editing the wizard
   * requires holding THIS page's lock; independent of the deck lock.
   */
  pageLockStatus: LockStatus;
  /**
   * Whether the page grain records have resolved. Until true, pageLockStatus reflects
   * pre-load defaults and must not drive redirects.
   */
  isPageLockStatusResolved: boolean;
}

export interface PageSetupProviderProps {
  children?: ReactNode;
}
